import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  type GroupProps,
  Loader,
  Modal,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useResizeObserver } from '@mantine/hooks';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { CancelButton } from 'components/CancelButton';
import { DeleteButton } from 'components/DeleteButton';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, tKeys, useTranslation } from 'locales';
import type { ReactNode } from 'react';
import { useHistory, useParams } from 'react-router';
import { isNullish } from 'remeda';

import { useDeleteAutomation, useLoadAutomation } from '../../api';
import { useEditAutomation } from '../AutomationWizard/controller';
import { ActionsPanel, ConditionsPanel, HistoryPanel } from './components';
import { InformationPanel } from './components/InformationPanel';

export const Automation = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [ref, rect] = useResizeObserver();

  const { automation, isAutomationInitialLoading } = useLoadAutomation({
    automationId: id,
  });
  const { deleteAutomation } = useDeleteAutomation();
  const onClickEdit = useEditAutomation();

  const [isModalOpen, { open: openModal, close: closeModal }] = useDisclosure(false);

  if (isNullish(automation))
    return (
      <Center>
        <Loader />
      </Center>
    );

  const handleDeleteAutomation = () => {
    if (isNullish(automation.id)) return;
    deleteAutomation(
      { id: automation.id },
      { onSuccess: () => history.push('/settings/automations') },
    );
  };

  return (
    <>
      <PageTitle>
        <PageTitle.Back />
        {automation.name}
        <PageTitle.Actions>
          <Tooltip label={t(i18nKeys.AUTOMATION.ACTIONS.DELETE)}>
            <ActionIcon variant="light" color="red" size="input-sm" onClick={openModal}>
              <IconTrash stroke={1.5} size={18} />
            </ActionIcon>
          </Tooltip>
          <Button leftSection={<IconEdit stroke={1.5} size={16} />} onClick={() => onClickEdit(id)}>
            {t(tKeys.EDIT)}
          </Button>
        </PageTitle.Actions>
      </PageTitle>
      <Group wrap="nowrap" w="100%" align="stretch">
        <Stack ref={ref} h="100%" flex={1}>
          <InformationPanel automation={automation} isLoading={isAutomationInitialLoading} />
          <ConditionsPanel automation={automation} isLoading={isAutomationInitialLoading} />
          <ActionsPanel automation={automation} isLoading={isAutomationInitialLoading} />
        </Stack>
        <Box mah={rect.height} flex={1} style={{ transition: 'all 0.3s' }}>
          <HistoryPanel automationId={id} refHeight={rect.height} />
        </Box>
      </Group>
      <Modal
        closeButtonProps={{
          tabIndex: -1,
        }}
        opened={isModalOpen}
        onClose={closeModal}
        title={t(tKeys.ATTENTION)}
      >
        <Text pb="lg">{t(tKeys.AUTOMATION.INDEX.DELETE_CONFIRMATION)}</Text>
        <Group mt="md" justify="end">
          <CancelButton onClick={closeModal} />
          <DeleteButton onClick={handleDeleteAutomation} />
        </Group>
      </Modal>
    </>
  );
};

type InfoRowProps = {
  label: ReactNode;
  children: ReactNode;
} & GroupProps;

export const InfoRow = ({ label, children, ...mantineGroupProps }: InfoRowProps) => (
  <Group align="start" justify="space-between" {...mantineGroupProps}>
    <Text size="sm">{label}</Text>
    {children}
  </Group>
);
